import axios from 'axios'
import { getSession, getSettings, getUser, getAuthToken } from '../_utils/get.store'
import { store } from '../_store'
import { authActions } from '../_store/auth/auth.slice'
import { getPlatform } from '../_utils/platform'
import { checkoutActions } from '../_store/checkout/checkout.slice'
import { FEATURED_BRANDS } from '../_utils/Constants'
import { activeBrandStore } from '../_store/brand/active-brand.store'

declare module 'axios' {
  interface AxiosRequestConfig {
    skipPlatformHeader?: boolean
  }
}

export const request = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
})
// this instance is added because it works when catching request's interceptors | request itself didn't work

// Track refresh state per shipper to handle concurrent requests
let isRefreshing = false
let refreshingShipperId: string | null = null
let refreshSubscribers: ((token: string | null) => void)[] = []

const onRefreshed = (token: string | null) => {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

const addRefreshSubscriber = (cb: (token: string | null) => void) => {
  refreshSubscribers.push(cb)
}

/**
 * Read Shipper from axios headers (AxiosHeaders is case-insensitive via .get).
 */
const readShipperHeader = (configHeaders: any): string | undefined => {
  if (!configHeaders) return undefined
  if (typeof configHeaders.get === 'function') {
    const viaGet = configHeaders.get('Shipper') || configHeaders.get('shipper')
    if (viaGet) return String(viaGet)
  }
  return configHeaders['Shipper'] || configHeaders['shipper'] || undefined
}

/**
 * Get the effective shipper ID for the current request
 * Priority: 1. Explicit header, 2. Active brand store, 3. Settings/Session
 */
const getEffectiveShipperId = (configHeaders: any): string => {
  const explicit = readShipperHeader(configHeaders)
  if (explicit) {
    return explicit
  }

  // Check active brand store (set by brand pages)
  const activeBrand = activeBrandStore.getShipperId()
  if (activeBrand) {
    return activeBrand
  }

  // Fall back to settings/session
  const settings = getSettings()
  const session = getSession()

  return settings?.shipper_id || session?.shipper_id || FEATURED_BRANDS['yaponamama']
}

request.interceptors.request.use(async (config) => {
  config.headers = config.headers || {}

  if (typeof window !== 'undefined') {
    // Determine the effective shipper ID for this request
    const shipperId = getEffectiveShipperId(config.headers)
    const explicitShipper = readShipperHeader(config.headers)

    // Set Shipper header if not already set
    if (!explicitShipper) {
      config.headers['Shipper'] = shipperId
    }

    // Store shipperId in config for use in error handler
    ; (config as any)._shipperId = shipperId

    // Handle Authorization header
    if (!config.headers['Authorization'] && typeof config.headers['Authorization'] === 'string') {
      // Explicitly set to empty string - keep it undefined
      config.headers['Authorization'] = undefined
    } else if (!config.headers['Authorization']) {
      // Get brand-specific token if available, otherwise fall back to global token
      const token = await getAuthToken(shipperId)

      if (token) {
        config.headers['Authorization'] = token
      }
    }
  }

  if (!config.skipPlatformHeader && !config.headers['Platform']) {
    const platformId = getPlatform()
    config.headers['Platform'] = platformId
  }

  return config
})

request.interceptors.response.use(
  (response) => response,
  (error) => errorHandler(error)
)

/**
 * Refresh token for a specific brand using mikaService
 */
export const refreshBrandToken = async (shipperId: string, globalToken: string) => {
  const { data } = await axios.post(
    `${process.env.NEXT_PUBLIC_BASE_URL}/v2/mika/refresh`,
    {
      shipper_id: shipperId,
      token: globalToken,
    }
  )
  return data
}

/**
 * Refresh the global token using customer refresh endpoint
 */
const refreshGlobalToken = async (refreshToken: string) => {
  const { data } = await axios.post(
    `${process.env.NEXT_PUBLIC_BASE_URL}/v1/customers/refresh-token`,
    {
      refresh_token: refreshToken,
    }
  )
  return data
}

const logout = () => {
  store.dispatch(authActions.logout())
  store.dispatch(checkoutActions.clear())
}

const errorHandler = async (error: any) => {
  const originalRequest = error.config

  if (error.response && typeof window !== 'undefined') {
    const { status } = error.response

    if ((status === 401 || status === 403) && !originalRequest._retry) {
      // Get the shipper ID from the original request
      const requestShipperId = originalRequest._shipperId || originalRequest.headers?.['Shipper']
      const activeBrand = activeBrandStore.getShipperId()
      const isBrandRequest = activeBrand && activeBrand !== FEATURED_BRANDS['yaponamama']

      // If already refreshing for this shipper, queue the request
      if (isRefreshing && refreshingShipperId === requestShipperId) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((token: string | null) => {
            if (token) {
              originalRequest.headers['Authorization'] = token
              originalRequest._retry = true
              resolve(request(originalRequest))
            } else {
              reject(error)
            }
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true
      refreshingShipperId = requestShipperId

      try {
        const user = getUser()

        // Determine refresh strategy based on whether this is a brand-specific request
        if (isBrandRequest && requestShipperId && requestShipperId !== FEATURED_BRANDS['yaponamama']) {
          // Brand-specific refresh using mikaService
          const globalToken = user?.access_token

          if (!globalToken) {
            console.log('No global token for brand refresh')
            isRefreshing = false
            refreshingShipperId = null
            return Promise.reject(error)
          }

          const data = await refreshBrandToken(requestShipperId, globalToken)

          if (data?.access_token) {
            // Update brand-specific token in store
            store.dispatch(
              authActions.setBrandToken({
                shipperId: requestShipperId,
                access_token: data.access_token,
                refresh_token: data.refresh_token,
                client_id: data.id,
              })
            )

            originalRequest.headers['Authorization'] = data.access_token
            onRefreshed(data.access_token)

            isRefreshing = false
            refreshingShipperId = null

            return request(originalRequest)
          }
        } else {
          // Global token refresh (yaponamama or non-brand pages)
          const refresh_token = user?.refresh_token

          if (!refresh_token) {
            console.log('No refresh token')
            isRefreshing = false
            refreshingShipperId = null
            logout()
            return Promise.reject(error)
          }

          const data = await refreshGlobalToken(refresh_token)

          store.dispatch(
            authActions.updateTokens({
              access_token: data.access_token,
              refresh_token: data.refresh_token,
              client_id: data.id || user?.id || '',
              shipperId: FEATURED_BRANDS['yaponamama'],
            })
          )

          originalRequest.headers['Authorization'] = data.access_token
          onRefreshed(data.access_token)

          isRefreshing = false
          refreshingShipperId = null

          return request(originalRequest)
        }
      } catch (refreshError) {
        isRefreshing = false
        refreshingShipperId = null
        onRefreshed(null)

        // Only logout if global token refresh failed
        if (!isBrandRequest) {
          logout()
        }

        if (process.env.NODE_ENV === 'development') {
          console.error('❌ Refresh Token Error:', refreshError)
        }

        return Promise.reject(error)
      }
    }
  }

  if (process.env.NODE_ENV === 'development') {
    const cfg = error?.config
    const url = cfg?.baseURL && cfg?.url ? `${cfg.baseURL}${cfg.url}` : cfg?.url
    console.error('❌ API Error:', {
      status: error.response?.status,
      url,
      params: cfg?.params,
      headers: cfg?.headers,
      data: error.response?.data,
    })
  }

  return Promise.reject(error.response || error)
}
